import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/portfolio/Reveal";
import { SITE, SERVICES, WHY_ME, PROCESS_STEPS, WA_LINK } from "@/lib/site";
import { Sparkles, Video, Megaphone, Image as ImageIcon, PenLine, Clock, Target, Heart, Check, ArrowUpRight, ChevronLeft, ChevronRight, Star } from "lucide-react";

const ICONS = {
  sparkles: Sparkles,
  video: Video,
  megaphone: Megaphone,
  image: ImageIcon,
  pen: PenLine,
  clock: Clock,
  target: Target,
  heart: Heart,
  check: Check,
};

export function TrustedBy() {
  return (
    <section className="py-14 md:py-20" data-testid="trusted-by-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <p className="eyebrow text-ink-soft mb-8">Brands I've created for</p>
        </Reveal>
        <div className="flex flex-wrap items-center gap-x-12 gap-y-6">
          {SITE.trustedBrands.map((b) => (
            <span
              key={b}
              data-testid={`brand-mark-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="font-serif text-2xl md:text-3xl text-ink/35 hover:text-ink transition-colors duration-500 select-none"
            >
              {b}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

export function About({ portraitUrl }) {
  const portrait = portraitUrl || SITE.portraitUrl;
  return (
    <section id="about" className="py-20 md:py-32 bg-ink text-cream" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <Reveal className="lg:col-span-5">
          <div className="max-w-md">
            <div className="rounded-[2rem] overflow-hidden">
              <img
                src={portrait}
                alt={`Portrait of ${SITE.name}`}
                loading="lazy"
                className="w-full aspect-[4/5] object-cover grayscale"
                data-testid="about-portrait"
              />
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.18em] text-cream/50">{SITE.location}</p>
          </div>
        </Reveal>
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal>
            <p className="eyebrow text-cream/50 mb-6">05 / The Creator</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
              About Me
            </h2>
            <p className="mt-7 text-lg leading-relaxed text-cream/85" data-testid="about-bio">
              {SITE.aboutBio}
            </p>
            <p className="mt-5 text-base leading-relaxed text-cream/60">
              {SITE.aboutBio2}
            </p>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-cta"
              className="mt-9 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold border-b border-cream/40 pb-1.5 hover:border-cream transition-colors duration-300"
            >
              Let's Work Together <ArrowUpRight className="w-4 h-4" />
            </a>
          </Reveal>
          <div className="mt-14 grid grid-cols-3 gap-6 border-t border-cream/15 pt-10">
            {SITE.stats.map((s, i) => (
              <Reveal key={s.label} delay={0.1 * i}>
                <p className="font-serif text-3xl sm:text-4xl md:text-5xl" data-testid={`stat-value-${i}`}>{s.value}</p>
                <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-cream/50">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function Services() {
  return (
    <section id="services" className="py-20 md:py-32 border-t border-cream-dark" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <p className="eyebrow text-ink-soft mb-6">02 / The Offer</p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink">
            What I Can Create
          </h2>
          <p className="mt-5 text-ink-soft leading-relaxed max-w-sm">
            High-performing ad creatives built for one outcome: revenue. Every concept is made to earn attention and convert it.
          </p>
        </Reveal>
        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-cream-dark border border-cream-dark">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon] || Sparkles;
            return (
              <Reveal key={s.title} delay={0.05 * i} className={`bg-cream ${i === SERVICES.length - 1 ? "sm:col-span-2" : ""}`}>
                <div className="p-8 md:p-10 h-full group" data-testid={`service-card-${i}`}>
                  <div className="flex items-start justify-between">
                    <p className="text-xs text-ink/40 mb-8">0{i + 1}</p>
                    <Icon className="w-5 h-5 text-ink/40 group-hover:text-ink transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-2xl md:text-[1.7rem] text-ink">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed max-w-xs">{s.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function WhyMe() {
  return (
    <section id="why-me" className="py-20 md:py-32 border-t border-cream-dark" data-testid="why-me-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <div>
              <p className="eyebrow text-ink-soft mb-6">03 / The Difference</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink">Why Work With Me</h2>
            </div>
            <p className="text-ink-soft max-w-xs leading-relaxed text-sm">
              The polish of a creative studio, with the pace and instinct of a creator who understands the feed.
            </p>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-cream-dark border border-cream-dark">
          {WHY_ME.map((w, i) => {
            const Icon = ICONS[w.icon] || Sparkles;
            return (
              <Reveal key={w.title} delay={0.06 * i} className="bg-cream">
                <div className="p-7 md:p-8 h-full" data-testid={`why-me-card-${i}`}>
                  <Icon className="w-5 h-5 text-ink/50 mb-10" strokeWidth={1.5} />
                  <h3 className="font-serif text-lg leading-snug text-ink">{w.title}</h3>
                  <p className="mt-2.5 text-[13px] text-ink-soft leading-relaxed">{w.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function Process() {
  return (
    <section id="process" className="py-20 md:py-32 border-t border-cream-dark" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <p className="eyebrow text-ink-soft mb-6">04 / How It Works</p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink">The Process</h2>
          <p className="mt-5 text-ink-soft leading-relaxed max-w-sm">
            A clear path from first idea to a finished creative you're proud to put behind your brand.
          </p>
        </Reveal>
        <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-cream-dark border border-cream-dark">
          {PROCESS_STEPS.map((p, i) => (
            <Reveal key={p.step} delay={0.08 * i} className="bg-cream">
              <div className="p-7 md:p-8 h-full" data-testid={`process-step-${i}`}>
                <p className="text-xs text-ink/40 mb-10">{p.step}</p>
                <h3 className="font-serif text-xl text-ink">{p.title}</h3>
                <p className="mt-2.5 text-[13px] leading-relaxed text-ink-soft">{p.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Testimonials() {
  const slides = SITE.testimonials;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const t = slides[index];

  const go = (dir) => setIndex((i) => (i + dir + slides.length) % slides.length);

  useEffect(() => {
  if (paused) return;

  const id = setInterval(() => {
    setIndex((i) => (i + 1) % slides.length);
  }, 8000);

  return () => clearInterval(id);
}, [paused, slides.length]);

  return (
    <section className="py-20 md:py-32 border-t border-cream-dark" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12 md:mb-16">
            <div>
              <p className="eyebrow text-ink-soft mb-6">06 / Kind Words</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-ink">What Businesses Say</h2>
              <p className="mt-4 text-sm text-ink-soft max-w-md leading-relaxed">
                Sample testimonials for demonstration — real client quotes are added as projects close. From local shops to D2C startups, across India.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => go(-1)} data-testid="testimonial-prev" aria-label="Previous testimonial"
                className="w-11 h-11 rounded-full border border-ink/20 flex items-center justify-center text-ink hover:bg-ink hover:text-cream transition-colors duration-300">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => go(1)} data-testid="testimonial-next" aria-label="Next testimonial"
                className="w-11 h-11 rounded-full border border-ink/20 flex items-center justify-center text-ink hover:bg-ink hover:text-cream transition-colors duration-300">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </Reveal>

        <div
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="testimonial-slideshow"
        >
          <AnimatePresence mode="wait">
            <motion.figure
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14"
              data-testid="testimonial-slide"
            >
              <blockquote className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-7">
                  <div className="flex items-center gap-0.5" data-testid="testimonial-stars" aria-label={`${t.rating} out of 5 stars`}>
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className={`w-4 h-4 ${s < t.rating ? "fill-ink text-ink" : "text-ink/20"}`} />
                    ))}
                  </div>
                  <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-semibold border border-ink/20 text-ink-soft px-3 py-1.5 rounded-full" data-testid="testimonial-sample-badge">
                    Sample Testimonial
                  </span>
                </div>
                <p className="font-serif text-2xl md:text-[2rem] leading-snug text-ink" data-testid="testimonial-quote">
                  “{t.quote}”
                </p>
                <figcaption className="mt-8 flex items-center gap-4">
                  <span className="w-12 h-12 shrink-0 bg-ink text-cream font-serif text-lg flex items-center justify-center rounded-xl" data-testid="testimonial-brand-mark">
                    {t.mark}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-ink" data-testid="testimonial-name">{t.name} · {t.role}</span>
                    <span className="block text-xs uppercase tracking-[0.16em] text-ink-soft mt-0.5" data-testid="testimonial-business">{t.business}</span>
                  </span>
                </figcaption>
              </blockquote>

              <div className="lg:col-span-5 flex flex-col justify-center gap-6 lg:border-l lg:border-cream-dark lg:pl-14">
                {[
                  ["What was made", t.made],
                  ["How it was used", t.used],
                ].map(([label, value]) => (
                  <div key={label}>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-ink-soft/70 mb-1.5">{label}</p>
                    <p className="text-sm leading-relaxed text-ink" data-testid={`testimonial-meta-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{value}</p>
                  </div>
                ))}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-ink-soft/70 mb-1.5">What happened</p>
                  <p className="text-sm leading-relaxed font-semibold text-ink bg-cream-dark/70 border-l-2 border-ink px-4 py-3" data-testid="testimonial-meta-what-happened">{t.result}</p>
                </div>
                <p className="eyebrow text-ink mt-2" data-testid="testimonial-category">{t.category}</p>
              </div>
            </motion.figure>
          </AnimatePresence>

          <div className="mt-12 flex items-center justify-between">
            <div className="flex items-center gap-2" data-testid="testimonial-dots">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  data-testid={`testimonial-dot-${i}`}
                  aria-label={`Go to testimonial ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-ink" : "w-1.5 bg-ink/20 hover:bg-ink/40"}`}
                />
              ))}
            </div>
            <p className="text-xs uppercase tracking-[0.18em] text-ink-soft" data-testid="testimonial-counter">
              {String(index + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Pricing() {
  return (
    <section className="py-20 md:py-32 border-t border-cream-dark" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <Reveal className="lg:col-span-8">
          <p className="eyebrow text-ink-soft mb-6">Investment</p>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight text-ink">
            Every project is custom — let's talk about your goals.
          </h2>
          <p className="mt-5 text-lg text-ink-soft max-w-xl">
            Rates depend on deliverables, usage and turnaround. Tell me what you're planning and I'll send a tailored quote within 24 hours.
          </p>
        </Reveal>
        <Reveal className="lg:col-span-4 lg:text-right" delay={0.1}>
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pricing-cta"
            className="inline-flex items-center gap-2 border border-ink text-ink px-7 py-3.5 rounded-full text-xs uppercase tracking-[0.14em] font-semibold hover:bg-ink hover:text-cream transition-colors duration-300"
          >
            Get a Tailored Quote
          </a>
        </Reveal>
      </div>
    </section>
  );
}
